import React from 'react';
import { Container, Typography, Box, Grid, Paper, Button } from '@mui/material';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faUniversity, faHandshake, faLaptopCode } from '@fortawesome/free-solid-svg-icons';

// Context
import { useI18n } from '../../context/I18nContext';

const SectionContainer = styled.section`
  padding: 5rem 0;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: var(--border-radius);
  margin: 2rem 0;
`;

const SponsorCard = styled(Paper)`
  padding: 2.5rem 2rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 2px dashed #ccc;
  min-height: 280px;
  background-color: #fff;
  
  &:hover {
    transform: translateY(-8px);
    border-color: var(--primary-color);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const BecomeSponsorButton = styled(Button)`
  margin-top: 3rem;
  padding: 0.8rem 2rem;
  font-weight: 500;
  font-size: 1rem;
  border-radius: 50px;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(0, 96, 100, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 96, 100, 0.3);
  }
`;

const SponsorsSection: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  
  const sponsors = [
    { 
      title: t('sponsor1_title'), 
      description: t('sponsor1_desc'),
      icon: faBuilding
    },
    { 
      title: t('sponsor2_title'), 
      description: t('sponsor2_desc'),
      icon: faUniversity
    },
    { 
      title: t('sponsor3_title'), 
      description: t('sponsor3_desc'),
      icon: faHandshake
    },
    { 
      title: t('sponsor4_title'), 
      description: t('sponsor4_desc'),
      icon: faLaptopCode
    }
  ];
  
  return (
    <SectionContainer id="sponsors">
      <Container>
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h2" align="center" gutterBottom color="primary">
            {t('sponsors_title')}
          </Typography>
          
          <Typography variant="h6" align="center" paragraph color="textSecondary">
            {t('sponsors_subtitle')}
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 5, mx: -2 }}>
            {sponsors.map((sponsor, index) => (
              <Box sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, px: 3, mb: 4, display: 'flex', justifyContent: 'center' }} key={index}>
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  <SponsorCard elevation={3}>
                    <FontAwesomeIcon 
                      icon={sponsor.icon} 
                      style={{ fontSize: '4rem', color: '#ccc', marginBottom: '1.5rem' }}
                    />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1.15rem', mb: 1.5 }}>
                      {sponsor.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.6 }}>
                      {sponsor.description}
                    </Typography>
                  </SponsorCard>
                </motion.div>
              </Box>
            ))}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <BecomeSponsorButton 
                onClick={() => {
                  navigate('/contact');
                  window.scrollTo(0, 0);
                }}
                variant="contained" 
                color="primary" 
                size="large"
              >
                {t('become_sponsor')}
              </BecomeSponsorButton>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    </SectionContainer>
  );
};

export default SponsorsSection;
